import { EMPTY, Observable, Subject, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';

import { IAuthorizedUserData } from '@project/view-models';
import { monitorLogger } from 'src/app/project/monitor/monitor-logger';
import { InfoDeviceService, LocalStorageService } from '@project/services';
import { DateFormatter } from '@core/helpers';
import { ApiService } from '@core/http';
import { environment } from '@env';

import { ChatUploadHelperService } from '../../services';
import {
  CALL_EVENTS,
  MESSAGE_EVENTS,
  SocketIoProviderEvents,
  SocketIoChatMessage,
  WebsocketChatMessage,
  WebSocketClient,
  WebsocketStatus,
  SocketIoCallMessage,
  WebsocketCallMessage,
} from '../../definitions';

export class SocketIoWebSocketProvider implements WebSocketClient {
  private socket: Socket | null = null;
  private _messages$ = new Subject<string>();
  private _status$ = new Subject<WebsocketStatus>();

  private readonly EVENTS_IN_USE: readonly SocketIoProviderEvents[] = [
    MESSAGE_EVENTS.MESSAGE_ADDED,
    CALL_EVENTS.CALL_STARTED,
    CALL_EVENTS.CALL_ENDED,
  ];

  public messages$ = this._messages$.asObservable();
  public status$ = this._status$.asObservable();

  get isOnline$() {
    return this.status$.pipe(map((status) => WebsocketStatus.Connected === status));
  }

  get isDown$() {
    return this.status$.pipe(map((status) => [WebsocketStatus.Disconnected, WebsocketStatus.Error].includes(status)));
  }

  constructor(
    private localstorageService: LocalStorageService,
    private apiService: ApiService,
    private chatUploadHelperService: ChatUploadHelperService,
    private infoDeviceService: InfoDeviceService,
  ) {}

  public connect(): Observable<void> {
    return timer(300).pipe(
      switchMap(() => {
        this.internalConnect();
        return EMPTY;
      }),
    );
  }

  public disconnect(): void {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;

    this._status$.next(WebsocketStatus.Disconnected);
    monitorLogger.socket.trackStatus(WebsocketStatus.Disconnected);
  }

  public destroy(): void {
    this.disconnect();
    this._status$.next(null);
  }

  public send(message: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (!this.socket || !this.socket.connected) {
      monitorLogger.app.console('WARN', 'Cannot send message: Socket is not connected');
      return;
    }

    monitorLogger.app.console(
      'WARN',
      'Ignoring send() because socket-io implementation does not support client-emitted socket messages.',
    );
  }

  public sendChatMessage(chatId: string, message: string): Observable<void> {
    return this.apiService.post(`${environment.environmentVariables.apiSocketUrl}/v1/chat/message`, {
      chatId,
      message,
    });
  }

  public sendChatAttachment(
    chatId: string,
    file: File,
    options?: { [key: string]: any; requisitionId?: string },
  ): Observable<void> {
    return this.chatUploadHelperService.uploadAttachmentFile(file, options?.requisitionId).pipe(
      switchMap((attachment) => {
        return this.apiService.post(`${environment.environmentVariables.apiSocketUrl}/v1/chat/attach`, {
          chatId,
          attachment: {
            downloadUrl: attachment.download_url,
            original: attachment.original,
            stored: attachment.stored,
            size: attachment.size,
          },
        });
      }),
    );
  }

  public sendServiceMessage(chatId: string, serviceMessage: string, ...additionalParams: string[]): Observable<void> {
    return this.apiService.post(`${environment.environmentVariables.apiSocketUrl}/v1/chat/service`, {
      type: serviceMessage,
      chatId,
      ...additionalParams,
    });
  }

  public getChatMessagesById(
    chatId: string,
    count: number,
    from: Date = new Date(),
  ): Observable<WebsocketChatMessage[]> {
    return this.apiService
      .get<SocketIoChatMessage[]>(
        `${
          environment.environmentVariables.apiSocketUrl
        }/v1/chat/messages/${chatId}/${count}/${DateFormatter.dateIncludeHours(from, 4)}`,
      )
      .pipe(map((messages) => messages.map((msg) => this.formatChatMessage(msg))));
  }

  private internalConnect(): void {
    const url = environment.environmentVariables.apiSocketUrl;

    if (this.socket) this.disconnect();

    this._status$.next(WebsocketStatus.Connecting);
    monitorLogger.socket.trackStatus(WebsocketStatus.Connecting, { url });

    const authData = this.getAuthData();
    const token = authData ? authData['accessToken'] : null;
    const profileType = authData ? authData['userType'] : null;
    const healthCenterId = this.getHealthCenterId();
    const deviceId = this.infoDeviceService.getDeviceId();

    this.socket = io(url, {
      reconnection: true,
      auth: {
        token,
        deviceId,
        recoverPendingEvents: false,
        metadata: {
          platform: 'web',
          profileType,
          userAgent: navigator.userAgent,
          application: 'teleconsulta',
          healthCenterId,
        },
      },
      transports: ['websocket'],
    });

    this.setupEventListeners();
  }

  private getAuthData(): IAuthorizedUserData {
    const authDataStr = this.localstorageService.getItem('auth');

    monitorLogger.app.console('LOG', `Retrieved auth data from localStorage: ${authDataStr ? 'found' : 'not found'}`);

    if (!authDataStr) {
      monitorLogger.app.console('WARN', 'No auth data found in localStorage');
      return null;
    }

    const accessToken = JSON.parse(authDataStr) as IAuthorizedUserData;

    if (!accessToken) {
      monitorLogger.app.console('WARN', 'Auth data found but no accessToken property');
      return null;
    }

    return accessToken;
  }

  private getHealthCenterId(): string {
    const hcId = this.localstorageService.getItem('healthCenterId');

    monitorLogger.app.console('LOG', `Retrieved health center ID from localStorage: ${hcId ? 'found' : 'not found'}`);
    if (!hcId) {
      monitorLogger.app.console('WARN', 'No health center ID found in localStorage');
      return null;
    }

    return hcId;
  }

  private setupEventListeners(): void {
    this.socket.on('connect', () => {
      this._status$.next(WebsocketStatus.Connected);
      monitorLogger.socket.trackStatus(WebsocketStatus.Connected);
    });

    this.socket.on('disconnect', (reason) => {
      this._status$.next(WebsocketStatus.Disconnected);
      monitorLogger.socket.trackStatus(WebsocketStatus.Disconnected, { reason });
    });

    this.socket.on('connect_error', (error) => {
      this._status$.next(WebsocketStatus.Error);
      monitorLogger.socket.trackError(error);
    });

    this.socket.on(MESSAGE_EVENTS.MESSAGE_ADDED, (data: SocketIoChatMessage, ackCallback?: () => void) => {
      if (ackCallback) ackCallback();

      const formattedMessage = this.formatChatMessage(data);
      const normalizedMessage = this.normalizeEventDataToSystemFormat(MESSAGE_EVENTS.MESSAGE_ADDED, formattedMessage);

      this._messages$.next(normalizedMessage);
      monitorLogger.socket.trackMessage(normalizedMessage);
    });

    this.socket.on(CALL_EVENTS.CALL_STARTED, (data: SocketIoCallMessage, ackCallback?: () => void) => {
      if (ackCallback) ackCallback();

      const formattedMessage = this.formatCallMessage(data);
      const normalizedMessage = this.normalizeEventDataToSystemFormat(CALL_EVENTS.CALL_STARTED, formattedMessage);

      this._messages$.next(normalizedMessage);
      monitorLogger.socket.trackMessage(normalizedMessage);
    });

    this.socket.on(CALL_EVENTS.CALL_ENDED, (data: SocketIoCallMessage, ackCallback?: () => void) => {
      if (ackCallback) ackCallback();

      const formattedMessage = this.formatCallMessage(data);
      const normalizedMessage = this.normalizeEventDataToSystemFormat(CALL_EVENTS.CALL_ENDED, formattedMessage);

      this._messages$.next(normalizedMessage);
      monitorLogger.socket.trackMessage(normalizedMessage);
    });

    this.socket.onAny((event: SocketIoProviderEvents, data: any, ackCallback?: () => void) => {
      if (this.EVENTS_IN_USE.includes(event)) return;

      if (ackCallback) ackCallback();

      const normalizedMessage = this.normalizeEventDataToSystemFormat(event, data);
      this._messages$.next(normalizedMessage);
      monitorLogger.socket.trackMessage(normalizedMessage);
      try {
      } catch (err) {
        console.error('Erro ao serializar mensagem do Socket.IO', err);
      }
    });
  }

  private formatChatMessage(message: SocketIoChatMessage): WebsocketChatMessage {
    return {
      user_id: message.userId,
      chat_id: message.chatId,
      text: message.text,
      attachment: message.attachment ? message.attachment : null,
      created: message.created,
      modified: message.modified,
    };
  }

  private formatCallMessage(data: SocketIoCallMessage): WebsocketCallMessage {
    return {
      chat_id: data.chatId,
      user_id: data.userId,
    };
  }

  private normalizeEventDataToSystemFormat(event: string, data: any) {
    let payloadObject: any;

    if (data?.message) {
      try {
        payloadObject = JSON.parse(data.message);
      } catch {
        payloadObject = { raw: data.message };
      }
    } else {
      payloadObject = data ?? {};
    }

    const normalizedPayload = { ...payloadObject, type: event };
    return JSON.stringify(normalizedPayload);
  }
}
